/**
 * `defineMessages` приложения: тот же вызов, что у react-intl (его узнаёт
 * `formatjs extract`), но описание остаётся «нетипизированным», а типы
 * аргументов берутся из реестра `FormatjsIntl.MessageArguments`, который
 * `npm run i18n:extract` выводит из ICU-строк каталога-источника
 * (`messageIds.generated.ts`).
 *
 * Зачем обёртка: `defineMessages` react-intl без явного дженерика объявляет
 * сообщение БЕЗ аргументов, и `t(m.title, { count })` не проходит `tsc`.
 * Через реестр `{count, plural, …}` сам становится `count: number`, а
 * забытый аргумент — ошибкой сборки, без ручных дженериков у каждого вызова.
 */
import type { ReactNode } from 'react';
import type { MessageId } from './messageIds.generated';

/** Тег разметки в сообщении (`<b>…</b>`): функция от содержимого тега. */
export type RichTag = (chunks: ReactNode[]) => ReactNode;

export interface Message<K extends MessageId = MessageId> {
  readonly id: K;
  readonly defaultMessage: string;
  readonly description?: string;
}

/** Id сообщений без аргументов: их можно подставлять как подпись (`label`). */
export type PlainMessageId = {
  [K in MessageId]: keyof FormatjsIntl.MessageArguments[K] extends never ? K : never;
}[MessageId];

/** Сообщение без аргументов — подпись пункта меню, кнопки, группы. */
export type PlainMessage = Message<PlainMessageId>;

export function defineMessages<const T extends Record<string, { id: MessageId; defaultMessage: string; description?: string }>>(
  messages: T,
): { readonly [P in keyof T]: Message<T[P]['id']> } {
  return messages;
}
